import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import { pdf } from "@react-pdf/renderer";
import NaturesCountReport from "../reports/NaturesCountReport";

// Today as "YYYY-MM-DD" in local time (date input max — no future dates).
const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// "2026-10-01" -> "1 Oct 2026"
const formatDay = (v) =>
  new Date(`${v}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const NaturesCountReportPage = () => {
  const [filters, setFilters] = useState({ from: "", to: "" });
  // The range the table currently reflects (empty = all time).
  const [applied, setApplied] = useState({ from: "", to: "" });
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const APP_BASE_URL = process.env.REACT_APP_URL;
  const today = todayLocal();

  const fetchReport = useCallback(async (params) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      // Send full-day boundaries in the user's local time as ISO timestamps,
      // so the server's timezone can't shift the range.
      const query = {};
      if (params.from) query.from = new Date(`${params.from}T00:00:00`).toISOString();
      if (params.to) query.to = new Date(`${params.to}T23:59:59.999`).toISOString();
      const response = await axios.get(`${API_BASE_URL}/backend/complaints/reports/natures-count`, {
        params: query,
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(response.data.data || []);
      setTotalCount(response.data.totalCount || 0);
      setApplied(params);
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      toast.error(error.response?.data?.message || "Failed to load nature of complaints report.");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const hasDate = Boolean(filters.from || filters.to);
  const isFiltered = Boolean(applied.from || applied.to);
  // Nothing new to apply when the inputs already match what is shown.
  const unchanged = filters.from === applied.from && filters.to === applied.to;

  const generateReport = () => {
    if (!hasDate) {
      toast.info("Select a From or To date to filter the report.");
      return;
    }
    if (filters.from && filters.to && filters.from > filters.to) {
      toast.error("From date cannot be after To date.");
      return;
    }
    if ((filters.from && filters.from > today) || (filters.to && filters.to > today)) {
      toast.error("Dates cannot be in the future.");
      return;
    }
    fetchReport(filters);
  };

  const appliedLabel = !isFiltered
    ? "All time"
    : applied.from && applied.to
      ? `${formatDay(applied.from)} – ${formatDay(applied.to)}`
      : applied.from
        ? `From ${formatDay(applied.from)}`
        : `Up to ${formatDay(applied.to)}`;

  useEffect(() => {
    fetchReport({ from: "", to: "" });
    // The all-time report loads once; the button applies subsequent filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxCount = useMemo(() => Math.max(...rows.map((row) => row.count), 1), [rows]);

  // PDF of exactly what the table shows (the APPLIED range, not unsubmitted
  // date inputs), so the user reviews the report first, then downloads it.
  const handleDownloadPdf = async () => {
    if (rows.length === 0) {
      toast.info("No data to export for this period.");
      return;
    }
    setDownloading(true);
    try {
      const generatedAt = new Date();
      let qrCodeUrl = "";
      try {
        qrCodeUrl = await QRCode.toDataURL(`${APP_BASE_URL}/reports/natures-count`, { width: 120 });
      } catch (qrErr) { /* QR is optional */ }

      const blob = await pdf(
        <NaturesCountReport
          reportData={rows}
          totalCount={totalCount}
          generatedAt={generatedAt}
          qrCodeUrl={qrCodeUrl}
          rangeLabel={appliedLabel}
        />
      ).toBlob();

      const range = isFiltered ? `${applied.from || "start"}_to_${applied.to || today}` : "All-Time";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Natures-Count-Report-${range}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded.");
    } catch (error) {
      console.error("Error building nature of complaints PDF:", error);
      toast.error("Error building the PDF report.");
    } finally {
      setDownloading(false);
    }
  };

  const clearFilters = () => {
    const emptyFilters = { from: "", to: "" };
    setFilters(emptyFilters);
    // Only refetch if the table is actually filtered.
    if (isFiltered) fetchReport(emptyFilters);
  };

  return (
    <div className="col-12">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-24">
        <div>
          <h6 className="mb-1 text-2xl fw-bold d-flex align-items-center gap-2">
            <Icon icon="mdi:shape-outline" /> Nature of Complaints
          </h6>
          <p className="text-secondary-light mb-0">Counts include non-informative complaints only.</p>
        </div>
        <div className="bg-success-focus text-success-main px-20 py-12 radius-8 fw-semibold">
          Total Complaints: {totalCount.toLocaleString()}
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-body row gy-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label">From date</label>
            <input type="date" className="form-control" value={filters.from}
              max={filters.to || today}
              onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label">To date</label>
            <input type="date" className="form-control" value={filters.to}
              min={filters.from || undefined}
              max={today}
              onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} />
          </div>
          <div className="col-md-4 d-flex gap-2">
            <button type="button" className="btn btn-success flex-grow-1" onClick={generateReport}
              disabled={loading || !hasDate || unchanged}
              title={!hasDate ? "Select a From or To date first" : unchanged ? "This range is already shown" : undefined}>
              {loading ? "Loading..." : "Generate Report"}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}
              disabled={loading || (!hasDate && !isFiltered)}>Clear</button>
          </div>
          <div className="col-12 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span className={`badge ${isFiltered ? "bg-primary-50 text-primary-600 border border-primary-100" : "bg-neutral-100 text-secondary-light"} px-12 py-8 radius-8 fw-semibold`}>
              <Icon icon="mdi:calendar-range" className="me-1" /> Showing: {appliedLabel}
            </span>
            <button type="button" className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
              onClick={handleDownloadPdf}
              disabled={loading || downloading || rows.length === 0}
              title={`Download the report shown below (${appliedLabel}) as PDF`}>
              {downloading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Preparing…
                </>
              ) : (
                <>
                  <Icon icon="mdi:file-pdf-box" style={{ fontSize: 18 }} /> Download PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead><tr><th>#</th><th>Complaint Nature</th><th>Count</th><th style={{ minWidth: 220 }}>Share</th></tr></thead>
              <tbody>
                {!loading && rows.map((row, index) => {
                  const percentage = totalCount ? (row.count / totalCount) * 100 : 0;
                  return (
                    <tr key={row.natureId || row.name}>
                      <td>{index + 1}</td>
                      <td className="fw-semibold">{row.name}</td>
                      <td>{row.count.toLocaleString()}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="progress flex-grow-1" style={{ height: 8 }}>
                            <div className="progress-bar bg-success-main" style={{ width: `${(row.count / maxCount) * 100}%` }} />
                          </div>
                          <span className="text-secondary-light" style={{ minWidth: 52 }}>{percentage.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {loading && <tr><td colSpan="4" className="text-center py-5">Loading report...</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan="4" className="text-center py-5">No complaints found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NaturesCountReportPage;
